import { type DeleteResourceRecordErrorBody } from '@Data/hooks/useDeleteResourceRecord';
import { Button } from '@Pages/pageRuntime/components/ui/Button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@Pages/pageRuntime/components/ui/dialog/AlertDialog';
import { isRecurringEventableRecord } from '@Pages/pageRuntime/hooks/recordUpdateInteractions';
import {
  useDeleteResourceRecord,
  type DeleteResourceRecordResource
} from '@Pages/pageRuntime/hooks/useDeleteResourceRecord';
import { useRecord } from '@Pages/pageRuntime/hooks/useRecord';
import { apiErrorMessage, isConflict } from '@Pages/pageRuntime/sdk/apiErrors';
import { canPerformResourceOperation } from '@Pages/pageRuntime/sdk/authorization';
import type {
  SdkApiError,
  SdkResource,
  SdkResourceQueryHandle
} from '@Pages/pageRuntime/sdk/types';
import { RecurrenceScopeInput } from '@Scheduling/components/RecurrenceScopeInput';
import React, { useState } from 'react';
import { toast } from 'sonner';

import {
  DocumentRelation,
  RecordDeletionEffectKind,
  RecordDeletionEffectKindUtils,
  RecurringEditScope,
  type RecurringEditScope as RecurringEditScopeValue
} from '@/types/enums';

type PreviewData = Diagonal.Data.Data.RecordDeletionPreviewData;
type RecordPlanEffectData = Diagonal.Data.Data.RecordPlanEffectData;
type RecordPlanItemData = Diagonal.Data.Data.RecordPlanItemData;

type DeleteResourceRecordProps = {
  /**
   * Resource handle returned by `Sdk.resource('<resourceId>')`.
   */
  resource: DeleteResourceRecordResource &
    SdkResourceQueryHandle<'show'> &
    Pick<SdkResource, 'policy_scope' | 'capabilities'>;

  /**
   * Record ID to delete.
   */
  recordId: string | null | undefined;

  /**
   * Optional already-loaded record. When omitted for an eventable resource, the
   * component loads the record when the confirmation dialog opens.
   */
  record?: Record<string, unknown>;

  /**
   * Button label.
   */
  label?: string;

  /**
   * Heading shown in the confirmation dialog.
   */
  confirmTitle?: React.ReactNode;

  /**
   * Supporting text shown in the confirmation dialog.
   */
  confirmDescription?: React.ReactNode;

  /**
   * Called after a successful deletion, before navigation.
   */
  onSuccess?: () => void;

  /**
   * Optional container className.
   */
  className?: string;
};

/**
 * A resource-aware delete action with preview and confirmation behavior. For a
 * recurring eventable record, the dialog automatically offers "this occurrence",
 * "this and future", and "all occurrences" scopes. Use for deleting resource
 * records instead of hand-built delete buttons. After deletion, navigates to the
 * resource records index.
 *
 * @example
 * const recordId = Sdk.helpers.useInput('record_id');
 * const students = Sdk.resource('students');
 *
 * return (
 *   <Sdk.Components.DeleteResourceRecord
 *     resource={students}
 *     recordId={recordId}
 *     label="Delete student"
 *   />
 * );
 */
export function DeleteResourceRecord({
  resource,
  recordId,
  record,
  label,
  confirmTitle,
  confirmDescription,
  onSuccess,
  className
}: DeleteResourceRecordProps) {
  const resourceId = resource?.id;
  const [isOpen, setIsOpen] = useState(false);
  const [recurrenceScope, setRecurrenceScope] = useState<RecurringEditScopeValue>(
    RecurringEditScope.This
  );
  const shouldLoadRecord =
    isOpen && Boolean(resource.capabilities?.eventable) && record === undefined;
  const {
    data: loadedRecord,
    isLoading: isRecordLoading,
    isError: isRecordError
  } = useRecord(resource, recordId, { enabled: shouldLoadRecord });
  const resolvedRecord = record ?? loadedRecord;
  const isRecurring =
    resolvedRecord !== undefined && isRecurringEventableRecord(resource, resolvedRecord);
  const isRecurrenceCheckLoading = shouldLoadRecord && isRecordLoading;
  const isRecurrenceCheckError = shouldLoadRecord && isRecordError;
  const {
    preview,
    isPreviewLoading,
    isPreviewError,
    confirm,
    isProcessing,
    deleteError,
    error,
    reset
  } = useDeleteResourceRecord({
    resource,
    recordId: recordId ?? undefined,
    fetchPreview: isOpen,
    onSuccess: () => {
      setIsOpen(false);
      // The delete API flow is client-driven and never round-trips through the
      // server's flash message, so surface the success toast here.
      toast.success('Record deleted.');
      onSuccess?.();
    }
  });

  function handleOpenChange(nextOpen: boolean): void {
    setIsOpen(nextOpen);
    if (!nextOpen) {
      reset();
      setRecurrenceScope(RecurringEditScope.This);
    }
  }

  function handleConfirm(event: React.MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    confirm(isRecurring ? { eventable: { scope: recurrenceScope } } : undefined);
  }

  if (!resourceId || !recordId) {
    return (
      <Button variant="destructive" disabled className={className}>
        {label ?? 'Delete'}
      </Button>
    );
  }

  if (!canPerformResourceOperation(resource, 'delete')) {
    return null;
  }

  const activePreview = deleteError ?? preview;
  const isBlocked = activePreview?.outcome.allowed === false || isConflict(error);

  return (
    <>
      <Button variant="destructive" onClick={() => setIsOpen(true)} className={className}>
        {label ?? 'Delete'}
      </Button>
      <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmTitle ?? deleteError?.title ?? 'Delete record'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <DeleteDialogDescription
                confirmDescription={confirmDescription}
                isPreviewLoading={isPreviewLoading}
                isPreviewError={isPreviewError}
                activePreview={activePreview}
                deleteError={deleteError}
                error={error}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>

          {isRecurrenceCheckError ? (
            <p role="alert" className="text-sm text-destructive">
              Unable to load this record’s recurrence details. Close the dialog and try again.
            </p>
          ) : null}

          {isRecurring ? (
            <div className="space-y-2">
              <p id="delete-recurrence-scope-label" className="text-sm font-medium">
                Choose how much of this recurring series to delete.
              </p>
              <RecurrenceScopeInput
                value={recurrenceScope}
                onChange={setRecurrenceScope}
                disabled={isProcessing}
                aria-labelledby="delete-recurrence-scope-label"
              />
            </div>
          ) : null}

          {activePreview && activePreview.effects.length > 0 ? (
            <DeletionEffectsList effects={activePreview.effects} />
          ) : null}

          <AlertDialogFooter
            start={
              isBlocked ? undefined : (
                <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
              )
            }
          >
            {isBlocked ? (
              <AlertDialogCancel variant="outline" disabled={isProcessing}>
                Cancel
              </AlertDialogCancel>
            ) : null}
            {!isBlocked ? (
              <AlertDialogAction
                variant="destructive"
                onClick={handleConfirm}
                disabled={
                  isProcessing ||
                  isPreviewLoading ||
                  isRecurrenceCheckLoading ||
                  isRecurrenceCheckError
                }
              >
                {label ?? 'Delete'}
              </AlertDialogAction>
            ) : null}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function DeleteDialogDescription({
  confirmDescription,
  isPreviewLoading,
  isPreviewError,
  activePreview,
  deleteError,
  error
}: {
  confirmDescription: React.ReactNode;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  activePreview: PreviewData | undefined;
  deleteError: DeleteResourceRecordErrorBody | null;
  error: SdkApiError | null;
}): React.ReactNode {
  const authoredDescription = confirmDescription ?? (deleteError ? undefined : 'Are you sure?');

  return (
    <div className="space-y-2">
      {authoredDescription ? <p>{authoredDescription}</p> : null}
      {error && !deleteError ? (
        <p role="alert" className="text-sm text-destructive">
          {apiErrorMessage(error, 'Unable to delete this record.')}
        </p>
      ) : null}
      {deleteError ? (
        <div className="space-y-1">
          <p>{deleteError.summary}</p>
          <p>{deleteError.detail}</p>
        </div>
      ) : isPreviewLoading ? (
        <p>Checking related records...</p>
      ) : isPreviewError ? (
        <p>Unable to check for related records. Try again.</p>
      ) : activePreview ? (
        <p>{activePreview.summary}</p>
      ) : null}
    </div>
  );
}

function DeletionEffectsList({ effects }: { effects: RecordPlanEffectData[] }): React.ReactNode {
  return (
    <ul
      data-testid="delete-resource-record-effects"
      className="max-h-72 space-y-3 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 text-sm"
    >
      {effects.map((effect) => (
        <li key={`${effect.kind}-${effect.field.id}`} className="space-y-1">
          <DeletionEffectHeader effect={effect} />
          {effect.items.length > 0 ? (
            <ul className="ml-4 list-disc space-y-0.5 text-muted-foreground">
              {effect.items.map((item) => (
                <DeletionEffectItem key={`${effect.field.id}-${item.id}`} item={item} />
              ))}
            </ul>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function DeletionEffectHeader({ effect }: { effect: RecordPlanEffectData }): React.ReactNode {
  const kindLabel = effectKindLabel(effect);

  return (
    <div className="flex flex-wrap items-baseline gap-1 font-medium">
      <span>{kindLabel}</span>
      <span>{effect.count}</span>
      <span>{effect.target.label ?? effect.target.resource}</span>
      <span className="text-muted-foreground">via {effect.field.label}</span>
    </div>
  );
}

function effectKindLabel(effect: RecordPlanEffectData): string {
  if (
    effect.kind === RecordDeletionEffectKind.Clear &&
    effect.field.type === DocumentRelation.BelongsToMany
  ) {
    return 'Will remove from';
  }

  if (!RecordDeletionEffectKindUtils.isValid(effect.kind)) {
    return String(effect.kind);
  }

  const labels: Record<RecordDeletionEffectKind, string> = {
    [RecordDeletionEffectKind.Block]: 'Blocked by',
    [RecordDeletionEffectKind.Clear]: 'Will clear',
    [RecordDeletionEffectKind.Archive]: 'Will archive'
  };

  return labels[effect.kind];
}

function DeletionEffectItem({ item }: { item: RecordPlanItemData }): React.ReactNode {
  return (
    <li>
      <span>{item.label ?? item.id}</span>
      {item.via ? (
        <span className="ml-1 text-xs italic">via {item.via.label ?? item.via.record}</span>
      ) : null}
    </li>
  );
}
